/**
 * Money primitives.
 *
 * Every monetary value in this system is an integer number of **kobo** held in a
 * `bigint`. Floating point is never used for money: `0.1 + 0.2 !== 0.3` is an
 * accounting defect waiting to happen, and this is a financial ledger.
 *
 * The database stores `Decimal(18, 2)` in naira; conversion happens only at the
 * persistence boundary (see `backend/src/common/money.transformer.ts`).
 */
export const KOBO_PER_NAIRA = 100n;
/** Largest value we accept, ₦999,999,999,999.99 — matches Decimal(18,2). */
export const MAX_KOBO = 99999999999999n;
export class MoneyError extends Error {
    constructor(message) {
        super(message);
        this.name = 'MoneyError';
    }
}
/**
 * Converts a naira amount to kobo.
 *
 * Accepts a string (preferred — no precision loss), a safe integer, or a number
 * with at most two decimal places. Rejects anything that would silently round.
 */
export function toKobo(naira) {
    const raw = typeof naira === 'number' ? formatNumberForParse(naira) : naira.trim();
    if (!/^-?\d+(\.\d{1,2})?$/.test(raw)) {
        throw new MoneyError(`Invalid naira amount "${naira}". Expected digits with at most two decimal places.`);
    }
    const negative = raw.startsWith('-');
    const [whole = '0', fraction = ''] = (negative ? raw.slice(1) : raw).split('.');
    const kobo = BigInt(whole) * KOBO_PER_NAIRA + BigInt(fraction.padEnd(2, '0'));
    return negative ? -kobo : kobo;
}
function formatNumberForParse(value) {
    if (!Number.isFinite(value)) {
        throw new MoneyError(`Invalid naira amount "${value}".`);
    }
    // toFixed(2) is safe here because we immediately reject anything that lost
    // precision, rather than accepting a silently rounded figure.
    const fixed = value.toFixed(2);
    if (Math.abs(Number(fixed) - value) > Number.EPSILON) {
        throw new MoneyError(`Naira amount ${value} has more than two decimal places and would be rounded.`);
    }
    return fixed;
}
/** Renders kobo as a plain decimal naira string, e.g. `57500.00`. Use for APIs and the DB. */
export function toNairaString(kobo) {
    const negative = kobo < 0n;
    const abs = negative ? -kobo : kobo;
    const whole = abs / KOBO_PER_NAIRA;
    const fraction = abs % KOBO_PER_NAIRA;
    return `${negative ? '-' : ''}${whole}.${fraction.toString().padStart(2, '0')}`;
}
/**
 * Renders kobo for display, e.g. `₦57,500.00`.
 *
 * `compact` renders large figures as ₦88.75M / ₦1.2B, matching how the
 * scholarship site presents cumulative totals.
 */
export function formatNaira(kobo, options = {}) {
    const { compact = false, showDecimals = true, symbol = '₦' } = options;
    const negative = kobo < 0n;
    const abs = negative ? -kobo : kobo;
    const sign = negative ? '-' : '';
    if (compact) {
        const naira = Number(abs / KOBO_PER_NAIRA);
        const units = [
            [1_000_000_000, 'B'],
            [1_000_000, 'M'],
            [1_000, 'K'],
        ];
        for (const [threshold, suffix] of units) {
            if (naira >= threshold) {
                const scaled = naira / threshold;
                const text = scaled >= 100 ? scaled.toFixed(0) : scaled.toFixed(2).replace(/\.?0+$/, '');
                return `${sign}${symbol}${text}${suffix}`;
            }
        }
    }
    const whole = (abs / KOBO_PER_NAIRA).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    if (!showDecimals)
        return `${sign}${symbol}${whole}`;
    const fraction = (abs % KOBO_PER_NAIRA).toString().padStart(2, '0');
    return `${sign}${symbol}${whole}.${fraction}`;
}
export function sumKobo(amounts) {
    return amounts.reduce((total, amount) => total + amount, 0n);
}
export function maxKobo(a, b) {
    return a > b ? a : b;
}
export function minKobo(a, b) {
    return a < b ? a : b;
}
export function absKobo(a) {
    return a < 0n ? -a : a;
}
export function isPositive(a) {
    return a > 0n;
}
/** Rounds up to the nearest multiple of `unit` kobo. `unit` must be positive. */
export function ceilToUnit(value, unit) {
    assertPositiveUnit(unit);
    const remainder = value % unit;
    return remainder === 0n ? value : value + (unit - remainder);
}
/** Rounds down to the nearest multiple of `unit` kobo. `unit` must be positive. */
export function floorToUnit(value, unit) {
    assertPositiveUnit(unit);
    return value - (value % unit);
}
function assertPositiveUnit(unit) {
    if (unit <= 0n)
        throw new MoneyError(`Rounding unit must be positive, received ${unit}.`);
}
/** Guards a value against the Decimal(18,2) column width before it reaches the DB. */
export function assertWithinRange(value, label = 'amount') {
    if (absKobo(value) > MAX_KOBO) {
        throw new MoneyError(`${label} ${toNairaString(value)} exceeds the maximum supported value.`);
    }
    return value;
}
/** Common rounding units, for loan-product configuration. */
export const ROUNDING_UNITS = {
    KOBO: 1n,
    NAIRA: 100n,
    TEN_NAIRA: 1000n,
    HUNDRED_NAIRA: 10000n,
    THOUSAND_NAIRA: 100000n,
};
//# sourceMappingURL=money.js.map